import { useMutation } from '@tanstack/react-query';
import { InitUploadRequest, InitUploadResponse } from '@/lib/upload/types.ts';
import { uploadToPresignedUrl } from '@/lib/upload/uploadToPresignedUrl.ts';
import { resolveContentType } from '@/lib/upload/resolveContentType.ts';

interface UseFileUploadOptions<TProcessResponse> {
  initUpload: (request: InitUploadRequest) => Promise<InitUploadResponse>;
  processUpload: (fileId: string) => Promise<TProcessResponse>;
  onSuccess?: (data: TProcessResponse) => void;
  onError?: (error: Error) => void;
}

export function useFileUpload<TProcessResponse>(
  options: UseFileUploadOptions<TProcessResponse>,
) {
  return useMutation<TProcessResponse, Error, File>({
    mutationFn: async (file: File) => {
      const contentType = resolveContentType(file);
      const initResponse = await options.initUpload({
        fileName: file.name,
        contentType,
        fileSize: file.size,
      });

      await uploadToPresignedUrl(initResponse.uploadUrl, file, contentType);

      return options.processUpload(initResponse.fileId);
    },
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}
